import moment from "moment";

export const BANNER_TYPES = [
  "hero_banner",
  "carousel",
  "slider",
  "grid_banner",
  "category_banner_grid",
];
export const HAND_TITLED_TYPES = [
  "carousel",
  "slider",
  "grid_banner",
  "category_banner_grid",
];
export const SLIDER_STYLE_TYPES = [
  "slider",
  "grid_banner",
  "category_banner_grid",
];
export const CODE_TYPES = ["html", "text"];
export const SKU_TYPES = ["best_in_category"];
export const ENTRY_TYPES = [...CODE_TYPES];
export const ITEM_TYPES = [...BANNER_TYPES, ...ENTRY_TYPES];
export const TITLE_SHOW_TYPES = [...SLIDER_STYLE_TYPES, ...ENTRY_TYPES];
export const COLUMN_TYPES = ["grid_banner", "slider", "category_banner_grid"];
export const FREE_COLUMN_TYPES = ["grid_banner", "category_banner_grid"];
export const HOVER_ANIMATION_TYPES = ["grid_banner"];
export const TEXT_ALIGN_TYPES = ["text"];
export const LOCAL_NAME_TYPES = SLIDER_STYLE_TYPES;
export const VALIDITY_TYPES = [
  "hero_banner",
  "slider",
  "grid_banner",
  "category_banner_grid",
  "html",
  "text",
];

export const COLUMN_OPTIONS = Array.from({ length: 8 }, (_, index) => ({
  value: index + 1,
  label: String(index + 1),
}));

export const DEFAULT_BACKGROUND = "#FFFFFF";
export const TRANSPARENT_BACKGROUND = "transparent";
export const DEFAULT_TEXT_COLOR = "#000000";
export const DEFAULT_TEXT_ALIGN = "left";
export const TEXT_ALIGN_OPTIONS = [
  { value: "left", label: "Left" },
  { value: "center", label: "Center" },
  { value: "right", label: "Right" },
  { value: "justify", label: "Justify" },
];
export const DEFAULT_PADDING = 24;
export const PLAIN_BACKGROUND_PADDING = 0;
export const MAX_PADDING = 200;

const PLAIN_BACKGROUNDS = ["#FFF", "#FFFFFF", "FFF", "FFFFFF", "WHITE", "TRANSPARENT"];

export const isTransparentBackground = (color) =>
  String(color || "").trim().toLowerCase() === TRANSPARENT_BACKGROUND;

export const isPlainBackground = (color) =>
  !color || PLAIN_BACKGROUNDS.includes(String(color).trim().toUpperCase());

export const defaultPaddingFor = (color) =>
  isPlainBackground(color) ? PLAIN_BACKGROUND_PADDING : DEFAULT_PADDING;

export const PADDING_RULES = [
  {
    validator: (_rule, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const padding = Number(value);
      if (!Number.isInteger(padding) || padding < 0 || padding > MAX_PADDING) {
        return Promise.reject(
          new Error(`Padding must be between 0 and ${MAX_PADDING}`)
        );
      }

      return Promise.resolve();
    },
  },
];

export const paddingValue = (value) =>
  value === undefined || value === null || value === "" ? undefined : value;

export const URL_PLACEHOLDER =
  "https://example.com/deals?utm_campaign=summer&utm_content=hero_banner";

let bannerCounter = 0;
export const makeBlockBanner = (overrides = {}) => {
  bannerCounter += 1;

  return {
    key: `banner_${bannerCounter}`,
    id: undefined,
    name: "",
    title: "",
    active: true,
    showTitle: true,
    content: "",
    hiddenContent: "",
    desktopImageFile: null,
    desktopImageUrl: null,
    mobileImageFile: null,
    mobileImageUrl: null,
    destinationUrl: "",
    ...overrides,
  };
};

export const startsWithBanner = (blockType) =>
  blockType === "hero_banner" || ENTRY_TYPES.includes(blockType);

export const DEFAULT_ITEMS_LIMIT = 6;
export const MAX_ITEMS_LIMIT = 50;

export const ITEMS_LIMIT_RULES = [
  {
    validator: (_rule, value) => {
      if (value === undefined || value === null || value === "") {
        return Promise.resolve();
      }

      const limit = Number(value);
      if (!Number.isInteger(limit) || limit < 1 || limit > MAX_ITEMS_LIMIT) {
        return Promise.reject(
          new Error(`Product count must be between 1 and ${MAX_ITEMS_LIMIT}`)
        );
      }

      return Promise.resolve();
    },
  },
];

let skuCounter = 0;
export const makeBlockSku = (overrides = {}) => {
  skuCounter += 1;

  return {
    key: `sku_${skuCounter}`,
    id: undefined,
    sku: "",
    title: "",
    ...overrides,
  };
};

export const hydrateBlockSkus = (items = []) =>
  [...items]
    .sort((a, b) => (a.position || 0) - (b.position || 0) || (a.id || 0) - (b.id || 0))
    .filter((item) => item.code)
    .map((item) => makeBlockSku({ id: item.id, sku: item.code }));

export const appendBlockSkus = (
  formData,
  { blockType, skus = [], loadedItems = [], itemPrefix }
) => {
  if (!SKU_TYPES.includes(blockType)) return;

  let itemIndex = 0;

  skus
    .filter((entry) => entry.sku)
    .forEach((entry, position) => {
      const prefix = itemPrefix(itemIndex);

      if (entry.id) formData.append(`${prefix}[id]`, entry.id);
      formData.append(`${prefix}[code]`, entry.sku);
      formData.append(`${prefix}[position]`, position);
      formData.append(`${prefix}[active]`, "true");
      formData.append(`${prefix}[show]`, "true");
      formData.append(`${prefix}[title]`, "");

      itemIndex += 1;
    });

  const keptIds = skus.map((entry) => entry.id).filter(Boolean);
  loadedItems
    .filter((item) => item.id && !keptIds.includes(item.id))
    .forEach((item) => {
      const prefix = itemPrefix(itemIndex);
      formData.append(`${prefix}[id]`, item.id);
      formData.append(`${prefix}[_destroy]`, "true");
      itemIndex += 1;
    });
};

export const normalizeDateForApi = (value) => {
  if (!value) return "";
  if (moment.isMoment(value)) return value.format("YYYY-MM-DD HH:mm:ss");
  return "";
};

export const toMomentRange = (from, to) => {
  if (!from && !to) return undefined;

  const fromDate = from ? moment(from) : null;
  const toDate = to ? moment(to) : null;
  if (fromDate && !fromDate.isValid()) return undefined;
  if (toDate && !toDate.isValid()) return undefined;

  return [fromDate, toDate];
};

export const hydrateBlockBanners = (blockType, items = [], fieldPrefixFor) => {
  const values = {};
  const banners = [...items]
    .sort((a, b) => (a.position || 0) - (b.position || 0))
    .map((item) => {
      const banner = makeBlockBanner({
        id: item.id,
        name: item.template_name || item.title || "",
        title: item.title || "",
        active: !!item.active,
        showTitle: !!item.show,
        content: item.content || "",
        hiddenContent: item.hidden_content || "",
        desktopImageUrl: item.desktop_image || null,
        mobileImageUrl: item.mobile_image || null,
        destinationUrl: item.destination_url || "",
      });
      const field = fieldPrefixFor(banner);

      values[`${field}_url`] = banner.destinationUrl;
      values[`${field}_title`] = banner.title;
      values[`${field}_content`] = banner.content;
      values[`${field}_hidden_content`] = banner.hiddenContent;
      values[`${field}_validity`] = toMomentRange(item.valid_from, item.valid_to);

      return banner;
    });

  if (!banners.length && startsWithBanner(blockType)) {
    banners.push(makeBlockBanner());
  }

  return { banners, values };
};

const appendImage = (formData, prefix, kind, file, url) => {
  if (file) {
    formData.append(`${prefix}[${kind}_file][data]`, file);
    formData.append(`${prefix}[${kind}_file][title]`, file.name);
    return;
  }
  if (!url) {
    formData.append(`${prefix}[${kind}_image]`, "");
    return;
  }
  if (!url.startsWith("blob:")) {
    formData.append(`${prefix}[${kind}_image]`, url);
  }
};

export const appendBlockItems = (
  formData,
  { blockType, banners, values, loadedItems = [], itemPrefix, fieldPrefixFor }
) => {
  if (!ITEM_TYPES.includes(blockType)) return;

  const append = (key, value) => {
    if (value === undefined || value === null || value === "") return;
    formData.append(key, value);
  };

  let itemIndex = 0;

  banners.forEach((banner, position) => {
    const prefix = itemPrefix(itemIndex);
    const field = fieldPrefixFor(banner);

    append(`${prefix}[id]`, banner.id);
    formData.append(`${prefix}[position]`, position);
    formData.append(`${prefix}[active]`, banner.active ? "true" : "false");
    formData.append(
      `${prefix}[show]`,
      TITLE_SHOW_TYPES.includes(blockType)
        ? banner.showTitle
          ? "true"
          : "false"
        : banner.active
        ? "true"
        : "false"
    );

    if (ENTRY_TYPES.includes(blockType)) {
      formData.append(`${prefix}[title]`, values[`${field}_title`] || "");
      formData.append(
        `${prefix}[content]`,
        values[`${field}_content`] || banner.content || ""
      );

      if (blockType === "text") {
        formData.append(
          `${prefix}[hidden_content]`,
          values[`${field}_hidden_content`] || banner.hiddenContent || ""
        );
      }

      itemIndex += 1;
      return;
    }

    formData.append(
      `${prefix}[destination_url]`,
      values[`${field}_url`] || ""
    );

    if (HAND_TITLED_TYPES.includes(blockType)) {
      formData.append(`${prefix}[template_name]`, banner.name || "");
    }

    if (blockType === "carousel") {
      const [bannerFrom, bannerTo] = Array.isArray(values[`${field}_validity`])
        ? values[`${field}_validity`]
        : [null, null];

      formData.append(`${prefix}[title]`, banner.name || "");
      formData.append(`${prefix}[valid_from]`, normalizeDateForApi(bannerFrom));
      formData.append(`${prefix}[valid_to]`, normalizeDateForApi(bannerTo));
    }

    if (SLIDER_STYLE_TYPES.includes(blockType)) {
      formData.append(`${prefix}[title]`, values[`${field}_title`] || "");
    }

    appendImage(
      formData,
      prefix,
      "desktop",
      banner.desktopImageFile,
      banner.desktopImageUrl
    );
    appendImage(
      formData,
      prefix,
      "mobile",
      banner.mobileImageFile,
      banner.mobileImageUrl
    );

    itemIndex += 1;
  });

  const keptIds = banners.map((banner) => banner.id).filter(Boolean);
  loadedItems
    .filter((item) => item.id && !keptIds.includes(item.id))
    .forEach((item) => {
      const prefix = itemPrefix(itemIndex);
      formData.append(`${prefix}[id]`, item.id);
      formData.append(`${prefix}[_destroy]`, "true");
      itemIndex += 1;
    });
};
